import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { SERVICES, serviceName } from "../../lib/constants";
import { fileToDataUrl } from "../../lib/upload";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { WheelSheet, WheelColumn } from "../../components/shared/WheelPicker";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Loader2, Upload, X, Minus, Plus,
  CalendarDays, Clock, Building2,
} from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TIME_WINDOWS = ["8:00 AM - 10:00 AM", "10:00 AM - 12:00 PM", "12:00 PM - 2:00 PM", "2:00 PM - 4:00 PM", "4:00 PM - 6:00 PM", "Flexible"];
const ITEM_CATALOG = ["Couch / Sofa", "Bed Frame", "Mattress", "Dresser", "Dining Set", "Appliances", "TV", "Desk", "Office Furniture", "Boxes", "Exercise Equipment", "Full Apartment"];
const SIZE_CHIPS = ["Single item", "Few items", "Studio", "1 Bedroom", "2 Bedroom", "3+ Bedroom", "Full house", "Office"];
const STEPS = ["Service", "Location", "Items", "Schedule", "Contact"];

const prettyDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
};

function DateSheet({ open, value, onCancel, onDone }) {
  const init = value ? new Date(value + "T12:00:00") : new Date();
  const [m, setM] = useState(init.getMonth());
  const [d, setD] = useState(init.getDate());
  const [y, setY] = useState(init.getFullYear());
  const yearNow = new Date().getFullYear();
  const dim = new Date(y, m + 1, 0).getDate();
  const day = Math.min(d, dim);
  const done = () => {
    let picked = new Date(y, m, day, 12);
    const floor = new Date(); floor.setHours(0, 0, 0, 0);
    if (picked < floor) picked = new Date();
    onDone(`${picked.getFullYear()}-${String(picked.getMonth() + 1).padStart(2, "0")}-${String(picked.getDate()).padStart(2, "0")}`);
  };
  return (
    <WheelSheet open={open} title="Preferred Date" onCancel={onCancel} onDone={done}>
      <WheelColumn className="flex-[1.5]" options={MONTHS.map((mm, i) => ({ value: i, label: mm }))} value={m} onChange={setM} />
      <WheelColumn className="flex-1" options={Array.from({ length: dim }, (_, i) => ({ value: i + 1, label: String(i + 1) }))} value={day} onChange={setD} />
      <WheelColumn className="flex-1" options={[yearNow, yearNow + 1].map((yy) => ({ value: yy, label: String(yy) }))} value={y} onChange={setY} />
    </WheelSheet>
  );
}

function TimeSheet({ open, value, onCancel, onDone }) {
  const [t, setT] = useState(value || TIME_WINDOWS[1]);
  return (
    <WheelSheet open={open} title="Preferred Time" onCancel={onCancel} onDone={() => onDone(t)}>
      <WheelColumn className="flex-1" options={TIME_WINDOWS.map((w) => ({ value: w, label: w }))} value={t} onChange={setT} />
    </WheelSheet>
  );
}

const chip = (active) =>
  `rounded-xl border px-4 py-3 text-sm font-semibold transition-all duration-150 text-left ${
    active ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/10 text-[var(--cc-accent)]" : "bg-card hover:border-foreground/25"}`;

export default function Quote() {
  const location = useLocation();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [items, setItems] = useState({});
  const [customItem, setCustomItem] = useState("");
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    service_type: location.state?.service || "",
    pickup_address: "", dropoff_address: "", preferred_date: "", time_window: "", property_size: "",
    stairs: false, stairs_flights: 1, elevator: false, details: "",
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setV = (k, v) => setForm({ ...form, [k]: v });

  const incItem = (name, delta) =>
    setItems((prev) => {
      const q = (prev[name] || 0) + delta;
      const next = { ...prev };
      if (q <= 0) delete next[name]; else next[name] = q;
      return next;
    });

  const addCustom = () => {
    const name = customItem.trim();
    if (!name) return;
    incItem(name, 1);
    setCustomItem("");
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    if (!files.length) return;
    const converted = await Promise.all(files.map((f) => fileToDataUrl(f, 1280).catch(() => null)));
    setPhotos((p) => [...p, ...converted.filter(Boolean)].slice(0, 6));
    e.target.value = "";
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/public/quote", {
        ...form,
        items: Object.entries(items).map(([name, qty]) => ({ name, qty })),
        special_items: Object.keys(items),
        flexible: form.time_window === "Flexible",
        photos,
      });
      setDone(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  };

  if (done)
    return (
      <div className="max-w-xl mx-auto px-5 py-28 text-center fade-up">
        <CheckCircle2 className="w-14 h-14 text-[var(--cc-accent)] mx-auto" />
        <h1 className="font-heading font-bold text-3xl mt-6" data-testid="quote-success-title">Request received.</h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">Your mission request is in our pipeline. A CastleCorp coordinator will respond with a flat-rate quote — usually within the hour during business hours.</p>
        <div className="mt-8 flex justify-center gap-4">
          {user ? (
            <Link to="/portal/quotes"><Button data-testid="quote-success-portal-button" className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">Track in Portal</Button></Link>
          ) : (
            <Link to="/register"><Button data-testid="quote-success-register-button" className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">Create Account to Track</Button></Link>
          )}
          <Link to="/"><Button variant="outline" className="rounded-full">Back Home</Button></Link>
        </div>
      </div>
    );

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const NavRow = ({ canNext = true, last = false }) => (
    <div className="flex justify-between pt-2">
      <Button variant="ghost" className="rounded-full h-12 px-6" onClick={back} data-testid="quote-back-button">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>
      {last ? (
        <Button data-testid="quote-submit-button" disabled={loading || !form.name || !form.email}
          className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold h-12 px-8 text-base"
          onClick={submit}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit Request <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      ) : (
        <Button data-testid="quote-next-button" disabled={!canNext}
          className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold h-12 px-8 text-base"
          onClick={next}>
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-5 py-14 lg:py-20">
      <p className="overline-cc mb-3">Request Quote</p>
      <h1 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight">Brief us on the mission.</h1>
      <p className="text-muted-foreground mt-3">Five quick steps. The more we know, the sharper your flat-rate quote.</p>

      <div className="flex items-center gap-1.5 mt-8 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-[var(--cc-accent)]" : "bg-border"}`} />
            <p className={`text-[11px] font-semibold mt-1.5 hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</p>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="fade-up">
          <h2 className="font-heading font-bold text-2xl mb-5">What do you need done?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {SERVICES.map((s) => (
              <button key={s.id} data-testid={`quote-service-${s.id}`}
                onClick={() => { setForm({ ...form, service_type: s.id }); setStep(1); }}
                className={`text-left rounded-xl border p-6 card-lift ${form.service_type === s.id ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/5" : "bg-card"}`}>
                <s.icon className="w-6 h-6 text-[var(--cc-accent)]" />
                <p className="font-heading font-bold text-lg mt-3">{s.name}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.blurb}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-7 fade-up">
          <h2 className="font-heading font-bold text-2xl">Where's the job?</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-sm font-bold">Pickup / service address</Label>
              <Input className="h-12 text-base" data-testid="quote-pickup-input" value={form.pickup_address} onChange={set("pickup_address")} placeholder="1420 Kettner Blvd, San Diego" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold">Dropoff address (if applicable)</Label>
              <Input className="h-12 text-base" data-testid="quote-dropoff-input" value={form.dropoff_address} onChange={set("dropoff_address")} placeholder="310 Mission Ave, Oceanside" />
            </div>
          </div>
          <div className="space-y-2.5">
            <Label className="text-sm font-bold">Size of the job</Label>
            <div className="flex flex-wrap gap-2">
              {SIZE_CHIPS.map((sz) => (
                <button key={sz} data-testid={`quote-size-${sz.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                  onClick={() => setV("property_size", form.property_size === sz ? "" : sz)}
                  className={chip(form.property_size === sz)}>{sz}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <button data-testid="quote-stairs-toggle" onClick={() => setV("stairs", !form.stairs)}
              className={`${chip(form.stairs)} flex items-center justify-between`}>
              <span>Stairs involved?</span>
              <span className={`text-xs font-mono-cc ${form.stairs ? "" : "text-muted-foreground"}`}>{form.stairs ? "YES" : "NO"}</span>
            </button>
            <button data-testid="quote-elevator-toggle" onClick={() => setV("elevator", !form.elevator)}
              className={`${chip(form.elevator)} flex items-center justify-between`}>
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Elevator available?</span>
              <span className={`text-xs font-mono-cc ${form.elevator ? "" : "text-muted-foreground"}`}>{form.elevator ? "YES" : "NO"}</span>
            </button>
          </div>
          {form.stairs && (
            <div className="flex items-center justify-between rounded-xl border bg-card px-4 py-3" data-testid="quote-flights-row">
              <p className="text-sm font-bold">Flights of stairs</p>
              <div className="flex items-center gap-3">
                <button data-testid="quote-flights-minus" onClick={() => setV("stairs_flights", Math.max(1, form.stairs_flights - 1))}
                  className="w-9 h-9 rounded-full border flex items-center justify-center hover:bg-secondary"><Minus className="w-4 h-4" /></button>
                <span className="font-heading font-bold text-xl w-6 text-center" data-testid="quote-flights-value">{form.stairs_flights}</span>
                <button data-testid="quote-flights-plus" onClick={() => setV("stairs_flights", Math.min(9, form.stairs_flights + 1))}
                  className="w-9 h-9 rounded-full border flex items-center justify-center hover:bg-secondary"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          <NavRow canNext={!!form.pickup_address.trim()} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-7 fade-up">
          <div>
            <h2 className="font-heading font-bold text-2xl">What are we handling?</h2>
            <p className="text-sm text-muted-foreground mt-1">Tap an item to add it — tap + for more than one.</p>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {ITEM_CATALOG.map((name) => {
              const qty = items[name] || 0;
              return qty === 0 ? (
                <button key={name} data-testid={`quote-item-${name.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                  onClick={() => incItem(name, 1)} className={`${chip(false)} text-center`}>{name}</button>
              ) : (
                <div key={name} className={`${chip(true)} flex items-center justify-between gap-2 px-3`}>
                  <button onClick={() => incItem(name, -1)} data-testid={`quote-item-minus-${name.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                    className="w-7 h-7 rounded-full border border-[var(--cc-accent)]/40 flex items-center justify-center shrink-0"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="truncate">{name}{qty > 1 ? ` × ${qty}` : ""}</span>
                  <button onClick={() => incItem(name, 1)} data-testid={`quote-item-plus-${name.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                    className="w-7 h-7 rounded-full border border-[var(--cc-accent)]/40 flex items-center justify-center shrink-0"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
          {Object.keys(items).filter((n) => !ITEM_CATALOG.includes(n)).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.keys(items).filter((n) => !ITEM_CATALOG.includes(n)).map((n) => (
                <span key={n} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/10 px-3 py-1.5 text-sm font-semibold">
                  {n}{items[n] > 1 ? ` × ${items[n]}` : ""}
                  <button onClick={() => setItems((p) => { const x = { ...p }; delete x[n]; return x; })}><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input className="h-12 text-base" data-testid="quote-custom-item-input" value={customItem}
              onChange={(e) => setCustomItem(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustom()} placeholder="Add other items..." />
            <Button variant="outline" className="h-12 rounded-xl px-5 font-semibold" onClick={addCustom} data-testid="quote-custom-item-add">Add</Button>
          </div>

          <div className="space-y-2.5">
            <Label className="text-sm font-bold">Upload photos (recommended)</Label>
            <p className="text-xs text-muted-foreground">Photos help us pre-size your job so your quote lands faster and sharper.</p>
            <label data-testid="quote-photo-upload"
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6 cursor-pointer hover:border-[var(--cc-accent)]/60 transition-colors text-sm font-semibold text-muted-foreground">
              <Upload className="w-4 h-4" /> Tap to upload photos of your items
              <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} />
            </label>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-20 h-20 object-cover rounded-lg border" />
                    <button data-testid={`quote-photo-remove-${i}`} onClick={() => setPhotos((x) => x.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold">Anything else we should know?</Label>
            <Textarea data-testid="quote-details-input" rows={3} className="text-base" value={form.details} onChange={set("details")}
              placeholder="Fragile pieces, parking constraints, timing notes..." />
          </div>
          <NavRow />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6 fade-up">
          <h2 className="font-heading font-bold text-2xl">When works for you?</h2>
          <button data-testid="quote-date-field" onClick={() => setDateOpen(true)}
            className="w-full flex items-center justify-between rounded-xl border bg-card px-5 py-5 hover:border-[var(--cc-accent)]/60 transition-colors">
            <span className="flex items-center gap-3 font-bold text-base"><CalendarDays className="w-5 h-5 text-[var(--cc-accent)]" /> Preferred date</span>
            <span className={`font-heading text-base ${form.preferred_date ? "font-bold" : "text-muted-foreground"}`}>
              {form.preferred_date ? prettyDate(form.preferred_date) : "Select date"}
            </span>
          </button>
          <button data-testid="quote-time-field" onClick={() => setTimeOpen(true)}
            className="w-full flex items-center justify-between rounded-xl border bg-card px-5 py-5 hover:border-[var(--cc-accent)]/60 transition-colors">
            <span className="flex items-center gap-3 font-bold text-base"><Clock className="w-5 h-5 text-[var(--cc-accent)]" /> Preferred time</span>
            <span className={`font-heading text-base ${form.time_window ? "font-bold" : "text-muted-foreground"}`}>
              {form.time_window || "Select window"}
            </span>
          </button>
          <p className="text-xs text-muted-foreground">Not locked in — your coordinator confirms the exact slot with you.</p>
          <NavRow />
          <DateSheet open={dateOpen} value={form.preferred_date} onCancel={() => setDateOpen(false)}
            onDone={(v) => { setV("preferred_date", v); setDateOpen(false); }} />
          <TimeSheet open={timeOpen} value={form.time_window} onCancel={() => setTimeOpen(false)}
            onDone={(v) => { setV("time_window", v); setTimeOpen(false); }} />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-6 fade-up">
          <h2 className="font-heading font-bold text-2xl">Where do we send your quote?</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-sm font-bold">Full name</Label>
              <Input className="h-12 text-base" data-testid="quote-name-input" value={form.name} onChange={set("name")} placeholder="Jordan Smith" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold">Phone</Label>
              <Input className="h-12 text-base" data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder="(619) 555-0100" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold">Email</Label>
            <Input className="h-12 text-base" data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
          </div>
          <div className="rounded-xl border bg-card p-5 space-y-1.5 text-sm" data-testid="quote-summary">
            <p className="font-heading font-bold text-base mb-2">Mission summary</p>
            <p><span className="text-muted-foreground">Service:</span> <span className="font-semibold">{serviceName(form.service_type)}</span></p>
            {form.pickup_address && <p><span className="text-muted-foreground">Route:</span> <span className="font-semibold">{form.pickup_address}{form.dropoff_address ? ` → ${form.dropoff_address}` : ""}</span></p>}
            {form.property_size && <p><span className="text-muted-foreground">Size:</span> <span className="font-semibold">{form.property_size}</span></p>}
            <p><span className="text-muted-foreground">Access:</span> <span className="font-semibold">{form.stairs ? `Stairs ×${form.stairs_flights}` : "No stairs"}{form.elevator ? " · Elevator" : ""}</span></p>
            {Object.keys(items).length > 0 && <p><span className="text-muted-foreground">Items:</span> <span className="font-semibold">{Object.entries(items).map(([n, q]) => (q > 1 ? `${n} ×${q}` : n)).join(", ")}</span></p>}
            {(form.preferred_date || form.time_window) && <p><span className="text-muted-foreground">Schedule:</span> <span className="font-semibold">{form.preferred_date ? prettyDate(form.preferred_date) : "Flexible date"}{form.time_window ? ` · ${form.time_window}` : ""}</span></p>}
            {photos.length > 0 && <p><span className="text-muted-foreground">Photos:</span> <span className="font-semibold">{photos.length} attached</span></p>}
          </div>
          <NavRow last />
        </div>
      )}
    </div>
  );
}
